/* ============================================================
   Fence Works — in-app activity (→ FW Admin customer timelines)

   What gets recorded, per signed-in user:
     session.start   a new visit (30 idle minutes starts another), with the
                     device class and screen size
     page.view       every screen opened, with how long the previous one held
     click           the visible label of a button / link pressed (never what
                     was typed; inputs, textareas and [data-no-track] are skipped)
     quote.* customer.* product.* job.* project.* settings.* company.*
                     things created or changed, found by diffing the store on
                     a genuine local edit (never a cloud sync) — counts,
                     statuses and totals, no names or addresses
     app.error       an uncaught error's message

   Events queue in memory and leave in batches (every 10s, at 25 events, and
   when the tab hides). Nothing is sent in the sandbox or when signed out.
   The server (server/src/activity.js) takes workspace and user from the
   session, not from here.
   ============================================================ */

type Ev = { e: string; t: number; r: string | null; s: string; p?: Record<string, any> };

const IDLE_MS = 30 * 60_000;
const FLUSH_MS = 10_000;
const BATCH = 25;
const SID_KEY = "fw_track_sid";

let queue: Ev[] = [];
let route: string | null = null;
let routeAt = 0;
let started = false;
let timer: any = null;
const lastOnce = new Map<string, number>();   // throttle key → last sent

// the platform owner's own FW Admin screens are not product usage
const ADMIN_ROUTES = new Set(["fwcustomers", "lifecycle", "growth", "support", "ops", "deletions", "printadmin", "swag", "leadengine", "leadengine-find", "leadengine-today", "prospect", "fwadmins", "leadengine-outreach", "fwstatus"]);
const now = () => Date.now();
const store = (): Storage | null => { try { return window.sessionStorage; } catch { return null; } };

/* The session id, rotated after 30 idle minutes. A new one emits session.start. */
function sessionId(): string {
  const ss = store();
  let raw: any = null;
  try { raw = ss && JSON.parse(ss.getItem(SID_KEY) || "null"); } catch { raw = null; }
  const t = now();
  if (raw && raw.id && t - raw.at < IDLE_MS) {
    raw.at = t;
    try { ss && ss.setItem(SID_KEY, JSON.stringify(raw)); } catch {}
    return raw.id;
  }
  const id = Math.random().toString(36).slice(2, 10) + t.toString(36);
  try { ss && ss.setItem(SID_KEY, JSON.stringify({ id, at: t })); } catch {}
  queue.push({ e: "session.start", t, r: route, s: id, p: device() });
  return id;
}

export function device() {
  const ua = navigator.userAgent || "";
  const os = /iPhone|iPad|iPod/.test(ua) ? "iOS" : /Android/.test(ua) ? "Android" : /Mac OS X/.test(ua) ? "Mac" : /Windows/.test(ua) ? "Windows" : /CrOS/.test(ua) ? "ChromeOS" : /Linux/.test(ua) ? "Linux" : "Other";
  const w = window.innerWidth;
  const kind = w < 700 ? "phone" : w < 1100 ? "tablet" : "desktop";
  let standalone = false;
  try { standalone = window.matchMedia("(display-mode: standalone)").matches || !!(navigator as any).standalone; } catch {}
  return { device: `${kind} · ${os}${standalone ? " · installed app" : ""}`, os, w, h: window.innerHeight };
}

const enabled = () => {
  const w: any = window;
  return !!(w.VFP_API_BASE && w.VFP_DB && w.VFP_DB.track && !(w.VFP_DEMO && w.VFP_DEMO.active));
};

/** Record one event. `once` throttles repeats of the same key (ms). */
export function track(e: string, p?: Record<string, any>, opts: { once?: string; within?: number } = {}) {
  try {
    if (!enabled()) return;
    if (route && ADMIN_ROUTES.has(route)) return;
    if (opts.once) {
      const last = lastOnce.get(opts.once) || 0;
      if (now() - last < (opts.within || 60_000)) return;
      lastOnce.set(opts.once, now());
    }
    queue.push({ e, t: now(), r: route, s: sessionId(), p: p && Object.keys(p).length ? p : undefined });
    if (queue.length >= BATCH) flush();
  } catch {}
}

export function flush() {
  if (!queue.length || !enabled()) return;
  const batch = queue.splice(0, 50);
  try {
    (window as any).VFP_DB.track.send(batch).then((ok: boolean) => { if (!ok && queue.length < 200) queue.unshift(...batch.slice(-25)); }).catch(() => {});
  } catch {}
}

/** Call on every route change. */
export function trackPage(r: string) {
  if (!r || r === route) return;
  const prev = route, held = routeAt ? now() - routeAt : 0;
  route = r; routeAt = now();
  track("page.view", prev ? { from: prev, prevSec: Math.round(held / 1000) } : undefined);
}

/* The visible label of what was clicked — never input values. */
export function clickLabel(target: any): string | null {
  const el = target && target.closest ? target.closest("button, a, [role=button], [role=tab], summary, label.chip, .chip, .sb-item, .sb-subitem") : null;
  if (!el || el.closest("[data-no-track]")) return null;
  const aria = el.getAttribute("aria-label") || el.getAttribute("title");
  const text = String(el.innerText || el.textContent || "").replace(/\s+/g, " ").trim();
  const label = (text || aria || "").slice(0, 60);
  return label || null;
}

/** Wire the automatic parts once: clicks, errors, flush timer, tab hide. */
export function initTracking() {
  if (started) return;
  started = true;
  document.addEventListener("click", (ev: any) => {
    const label = clickLabel(ev.target);
    if (label) track("click", { label });
  }, { capture: true, passive: true } as any);
  window.addEventListener("error", (ev: any) => { track("app.error", { message: String((ev && ev.message) || "error").slice(0, 200) }, { once: "err:" + String(ev && ev.message).slice(0, 60), within: 300_000 }); });
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") flush(); });
  window.addEventListener("pagehide", () => flush());
  timer = setInterval(flush, FLUSH_MS);
}

/* ------------------------------------------------------------
   Store diff → action events. Pure (exported for the unit test).
   ------------------------------------------------------------ */
const byId = (xs: any[]) => { const m = new Map<string, any>(); for (const x of xs || []) if (x && x.id != null) m.set(String(x.id), x); return m; };
const SENT = new Set(["sent", "viewed", "signed", "paid", "won", "lost"]);

export function diffActions(prev: any, next: any): { e: string; p?: any; once?: string }[] {
  const out: { e: string; p?: any; once?: string }[] = [];
  if (!prev || !next) return out;
  // quotes
  const pq = byId(prev.quotes), nq = byId(next.quotes);
  for (const [id, q] of nq) {
    const was = pq.get(id);
    const total = Number(q.total || q.grandTotal || 0) || undefined;
    if (!was) {
      out.push({ e: "quote.created", p: { total, status: q.status || null } });
      if (q.status === "sent") out.push({ e: "quote.sent", p: { total } });   // created and sent in one go
      continue;
    }
    if ((was.status || "") !== (q.status || "")) {
      if (q.status === "sent" && !SENT.has(was.status)) out.push({ e: "quote.sent", p: { total } });
      else out.push({ e: "quote.status", p: { from: was.status || null, to: q.status || null, total } });
    }
  }
  for (const id of pq.keys()) if (!nq.has(id)) out.push({ e: "quote.deleted" });
  // customers
  const pc = byId(prev.customers), nc = byId(next.customers);
  let addedC = 0; for (const id of nc.keys()) if (!pc.has(id)) addedC++;
  if (addedC) out.push({ e: "customer.added", p: addedC > 1 ? { n: addedC } : undefined });
  // products
  const pp = byId(prev.customProducts), np = byId(next.customProducts);
  for (const [id, pr] of np) {
    const was = pp.get(id);
    if (!was) out.push({ e: "product.created", p: { category: pr.category || null } });
    else if (JSON.stringify(was) !== JSON.stringify(pr)) out.push({ e: "product.edited", once: "product.edited:" + id });
  }
  for (const id of pp.keys()) if (!np.has(id)) out.push({ e: "product.deleted" });
  // estimates (projects)
  const pj = byId(prev.projects), nj = byId(next.projects);
  for (const id of nj.keys()) if (!pj.has(id)) out.push({ e: "project.saved" });
  // jobs
  const pw = byId(prev.workOrders), nw = byId(next.workOrders);
  for (const [id, w] of nw) {
    const was = pw.get(id);
    if (!was) out.push({ e: "job.scheduled" });
    else if ((was.status || "") !== (w.status || "")) out.push({ e: "job.status", p: { from: was.status || null, to: w.status || null } });
  }
  // settings / company — which keys, throttled (a form fires on every keystroke)
  if (prev.settings !== next.settings && prev.settings && next.settings) {
    const keys = Object.keys({ ...prev.settings, ...next.settings }).filter((k) => JSON.stringify(prev.settings[k]) !== JSON.stringify(next.settings[k])).slice(0, 8);
    if (keys.length) out.push({ e: "settings.changed", p: { keys }, once: "settings:" + keys.join(",") });
  }
  if (prev.company !== next.company && JSON.stringify(prev.company) !== JSON.stringify(next.company)) out.push({ e: "company.edited", once: "company" });
  return out;
}

/** Feed from the store's local-edit path. */
export function trackStateChange(prev: any, next: any) {
  try {
    for (const a of diffActions(prev, next)) track(a.e, a.p, a.once ? { once: a.once, within: 120_000 } : {});
  } catch {}
}

export function _resetForTest() { queue = []; route = null; routeAt = 0; lastOnce.clear(); if (timer) clearInterval(timer); started = false; }
